// modals/HelpModal.js
import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { handleAddToHomeScreen, canPromptPwaInstall, promptPwaInstall, isMobile } from '../utils/deviceUtils';
import { getModalVariants, overlayVariants, buttonVariants } from '../utils/animationUtils';

const HelpModal = ({ onClose }) => {
    const handleConfirm = () => {
        localStorage.setItem('hasSeenHelp', 'true');
        onClose();
    };

    const handleShareApp = () => {
        // 데스크톱인 경우 알림 표시
        if (!isMobile()) {
            alert('앱 추천하기 기능은 모바일에서만 사용할 수 있습니다.\n모바일 기기에서 접속해주세요.');
            return;
        }
        
        // 앱 추천하기 페이지로 이동
        onClose();
        window.location.href = '/share-promotion';
    };

    // 샘플 데이터 삭제 기능은 사용자 혼동 방지를 위해 제거되었습니다.

    return (
        <AnimatePresence>
            <motion.div
                className="fixed inset-0 z-50 flex items-center justify-center"
                variants={overlayVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
            >
                {/* 배경 오버레이 */}
                <motion.div
                    className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                    onClick={onClose}
                ></motion.div>

                {/* 모달 컨텐츠 */}
                <motion.div
                    className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto"
                    variants={getModalVariants()}
                    initial="hidden"
                    animate="visible"
                    exit="exit"
                >
                    {/* 헤더 */}
                    <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                                <span className="text-2xl">💡</span>
                            </div>
                            <h2 className="text-xl font-bold text-gray-800">도움말</h2>
                        </div>
                        <motion.button
                            onClick={handleConfirm}
                            className="p-2 hover:bg-gray-100 rounded-xl transition-colors duration-200"
                            variants={buttonVariants}
                            whileHover="hover"
                            whileTap="tap"
                        >
                            <X className="w-5 h-5 text-gray-600" />
                        </motion.button>
                    </div>

                    {/* 컨텐츠 */}
                    <div className="p-6">
                        <div className="space-y-4 text-gray-700 leading-relaxed">
                            <div className="flex items-start gap-3">
                                <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">1</div>
                                <div>본 서비스는 <a href="https://previaworks.netlify.app/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">프리비아웍스</a>에서 제공하는 고객 및 영업관리 통합 솔루션입니다.</div>
                            </div>

                            <div className="flex items-start gap-3">
                                <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">2</div>
                                <div>입력하시는 모든 데이터는 FC님 본인의 기기에만 저장되고, 외부로 전송되지 않으니 안심하고 쓰셔도 됩니다.</div>
                            </div>

                            <div className="flex items-start gap-3">
                                <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">3</div>
                                <div>FC님 본인의 PC를 추가기기로 등록하면 데이터 동기화를 통해 편리하게 쓸 수 있습니다.(활동중엔 폰으로, 사무실에선 PC로 작업가능)</div>
                            </div>

                            <div className="flex items-start gap-3">
                                <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">4</div>
                                <div>동기화 기능을 통해 핸드폰을 바꿔도 계속 사용할 수 있습니다.</div>
                            </div>

                            <div className="flex items-start gap-3">
                                <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">5</div>
                                <div>체험기간이 종료되어도 데이터는 FC님 본인의 기기에만 남습니다.</div>
                            </div>
                        </div>
                    </div>

                    {/* 푸터 (섹션 분리 제거) */}
                    <div className="p-6 space-y-3">
                        <button
                            onClick={async () => {
                                if (canPromptPwaInstall()) {
                                    const res = await promptPwaInstall();
                                    if (res.outcome !== 'accepted') {
                                        // 사용자가 거절/닫음 → 안내로 폴백
                                        handleAddToHomeScreen();
                                    }
                                } else {
                                    handleAddToHomeScreen();
                                }
                            }}
                            className="w-full bg-gradient-to-r from-green-500 to-green-600 text-white py-3 px-4 rounded-xl font-bold hover:from-green-600 hover:to-green-700 transition-all duration-300 shadow-lg shadow-green-500/25 hover:shadow-green-500/40"
                        >
                            📱 홈 화면에 추가
                        </button>
                        <a
                            href="http://pf.kakao.com/_XTTxoxj/110889711"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block w-full bg-gradient-to-r from-purple-500 to-purple-600 text-white py-3 px-4 rounded-xl font-bold hover:from-purple-600 hover:to-purple-700 transition-all duration-300 shadow-lg shadow-purple-500/25 hover:shadow-purple-500/40 text-center"
                        >
                            🚀 FC퍼널 소개
                        </a>
                        <button
                            onClick={handleShareApp}
                            className="w-full bg-gradient-to-r from-yellow-400 to-yellow-500 text-black py-3 px-4 rounded-xl font-bold hover:from-yellow-500 hover:to-yellow-600 transition-all duration-300 shadow-lg shadow-yellow-400/25 hover:shadow-yellow-400/40 flex items-center justify-center gap-2"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                            </svg>
                            앱 공유하기
                        </button>
                        <button
                            onClick={handleConfirm}
                            className="w-full bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 px-4 rounded-xl font-bold hover:from-blue-600 hover:to-blue-700 transition-all duration-300 shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40"
                        >
                            확인
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
};

export default HelpModal;